/**
 * This component renders the roster tab for a club page
 * WARNING - Currently using some preprocessing of player data. may need to update this with use of API later
 */
import React, { useState } from "react";
import PlayerCard from "./RosterPlayerCard";
import { useInfiniteQuery } from "@tanstack/react-query";
import Loading from "../core/Loading";
import DropdownSelect from "../core/DropdownSelect";

const parseData = (data) => {
  data.forEach((player) => {
    // ADD GP - iterate player.player.stats and get games from each, add them together
    player.gp = 0;
    player.player.stats.forEach((stat) => {
      player.gp += stat.games;
    });

    // ADD GOALS - iterate player.player.stats and get games from each, add them together
    player.g = 0;
    player.player.stats.forEach((stat) => {
      player.g += stat.goals;
    });

    // ADD ASSISTS - iterate player.player.stats and get games from each, add them together
    player.a = 0;
    player.player.stats.forEach((stat) => {
      player.a += stat.assists;
    });

    // ADD PIMS - iterate player.player.stats and get games from each, add them together
    player.pims = 0;
    player.player.stats.forEach((stat) => {
      player.pims += stat.pims;
    });

    // ADD POINTS - iterate player.player.stats and get games from each, add them together
    player.points = 0;
    player.player.stats.forEach((stat) => {
      player.points += stat.goals + stat.assists;
    });

    player.reg_date = player.player.reg_date;

  });
};

const ClubRoster = ({ clubData, clubId }) => {
  const [sortBy, setSortBy] = useState("team");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortString, setSortString] = useState(true);

  // DR - Distinct list of team names so team dropdown is limited to ones matching
  const [team, setTeam] = useState("all");
  const [season, setSeason] = useState(clubData.roster_seasons[0].value);

  const onSeasonChange = (e) => {
    setSeason(e.target.value);
  };

  const fetchRoster = async (query) => {
    return await fetch(
      `${process.env.REACT_APP_BUIHA_API_URL}/club/${clubId}/roster/?offset=${
        query.pageParam ? query.pageParam : 0
      }&season=${season}`
    ).then((res) => res.json());
  };

  const {
    data, // data accumulated across all pages: InfiniteQueryData<Page>
    error, // any Error that has occurred
    fetchNextPage, // Function to fetch the next page
    hasNextPage, // Boolean that indicates if the next page is available
    isFetching,
  } = useInfiniteQuery({
    staleTime: Infinity,
    queryKey: ["roster", clubId, season],
    queryFn: fetchRoster,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.nextCursor;
    },
  });

  // Sort players by a certain value in a certain order
  function sortPlayers(playerList) {
    let list = [...playerList];

    const getProperty = (obj, prop) =>
      prop.includes(".")
        ? obj[prop.split(".")[0]][prop.split(".")[1]]
        : obj[prop];

    return list.sort((a, b) => {
      const valueA = getProperty(a, sortBy);
      const valueB = getProperty(b, sortBy);

      if (sortOrder === "asc") {
        return sortString ? valueA.localeCompare(valueB) : valueA - valueB;
      } else {
        return sortString ? valueB.localeCompare(valueA) : valueB - valueA;
      }
    });
  }

  function setSorts(by, order, string = true) {
    setSortBy(by);
    setSortOrder(order);
    setSortString(string);
  }

  // Define club gradient
  const gradientStyle = {
    background: `linear-gradient(to right, ${clubData.primary_color},
            ${clubData.secondary_color})`,
  };

  const loadingOptions = [
    { value: "all", label: "Loading...", disabled: true },
  ];

  const seasonOptions = clubData.roster_seasons.map((s) => ({
    value: s.value,
    label: s.title,
  }));

  if (!data)
    return (
      <>
        <div className="w-full flex flex-row items-start justify-center sm:justify-between mb-2 sm:mb-3">
          <div className="sm:block flex flex-col items-start justify-start">
            <h2 className="leading-tight hidden sm:block text-left text-2xl font-semibold">
              Club Roster
            </h2>
            <p className="hidden sm:block text-left dark-text">Loading...</p>
          </div>
          <div className="flex flex-row items-start justify-start">
            <DropdownSelect
              label="Season"
              value="all"
              onChange={() => {}}
              options={loadingOptions}
              disabled={true}
              className="mr-1 xxs:mr-2"
            />
            <DropdownSelect
              label="Team"
              value="all"
              onChange={() => {}}
              options={loadingOptions}
              disabled={true}
              className="mr-1 xxs:mr-2"
            />
            <DropdownSelect
              label="Sort By"
              value="all"
              onChange={() => {}}
              options={loadingOptions}
              disabled={true}
            />
          </div>
        </div>
        <Loading />
      </>
    );

  if (error) return "An error has occurred: " + error.message;

  if (hasNextPage && !isFetching) {
    fetchNextPage();
  }

  const dataPlayers = data.pages.flatMap((page) => page.players);

  parseData(dataPlayers);

  const dataTeams = Array.from(new Set(dataPlayers.map((x) => x.team)));

  // DR - Seperating this out in an attempt to count the rendered links
  const visiblePlayers = sortPlayers(dataPlayers)
    .filter((player) => team === "all" || player.team === team)
    .map((player) => (
      <PlayerCard
        key={player.player.id} // Use player.id as the key for stable re-renders
        player={player}
        club_data={clubData}
        sortBy={sortBy}
        gradientStyle={gradientStyle}
      />
    ));

  const teamOptions = [
    { value: "all", label: "All Teams" },
    ...clubData.teams
      .filter((team) => dataTeams.indexOf(team.name) > -1)
      .map((team) => ({
        value: team.name,
        label: team.name.split(" ").slice(1).join(" "), // Remove the first word - TODO - make this more robust
      })),
  ];

  const onTeamChange = (e) => {
    setTeam(e.target.value);
  };

  const onSortChange = (e) => {
    const sortByValue = e.target.value;

    switch (sortByValue) {
      case 'team':
        setSorts(sortByValue, 'asc');
        break;
      case 'surname':
        setSorts('player.surname', 'asc');
        break;
      case 'forename':
        setSorts('player.forename', 'asc');
        break;
      case 'institution':
        setSorts(sortByValue, 'asc');
        break;
      case 'classification':
        setSorts(sortByValue, 'asc');
        break;
      case 'reg_date':
        setSorts('reg_date', 'asc');
        break;
      case 'gp':
        setSorts(sortByValue, 'desc', false);
        break;
      case 'points':
        setSorts(sortByValue, 'desc', false);
        break;
      case 'g':
        setSorts(sortByValue, 'desc', false);
        break;
      case 'a':
        setSorts(sortByValue, 'desc', false);
        break;
      case 'pims':
        setSorts(sortByValue, 'desc', false);
        break;
      default:
        // Handle default case if needed
        break;
    }

    setSortBy(sortByValue); // Update the state to reflect the current sort
  };

  const sortOptions = [
    { value: 'surname', label: 'Surname' },
    { value: 'forename', label: 'Forename' },
    { value: 'team', label: 'Team' },
    { value: 'institution', label: 'Institution' },
    { value: 'classification', label:'Status' },
    { value: 'reg_date', label: 'First Reg' },
    { value: 'gp', label: 'Games' },
    { value: 'points', label: 'Points' },
    { value: 'g', label: 'Goals' },
    { value: 'a', label: 'Assists' },
    { value: 'pims', label: 'PIMs' },
  ];

  return (
    data && (
      <>
        <div className="flex w-full flex-row items-start justify-center sm:justify-between mb-2 sm:mb-3">
          <div className="sm:block flex flex-col items-start justify-start">
            <h2 className="leading-tight hidden sm:block text-left text-2xl font-semibold">
              Club Roster
            </h2>
            <p className="hidden sm:block text-darken font-light">
              Displaying{" "}
              {React.Children.toArray(visiblePlayers).filter(Boolean).length}{" "}
              players
            </p>
          </div>
          <div className="flex flex-row items-start justify-start">
            {/* Select season */}
            <DropdownSelect
              label="Season"
              options={seasonOptions}
              value={season}
              onChange={onSeasonChange}
              className="mr-1 xs:mr-2"
            />

            {/* Select team */}
            <DropdownSelect
              label="Team"
              options={teamOptions}
              value={team}
              onChange={onTeamChange}
              className="mr-1 xs:mr-2"
            />

            {/* Select order */}
            <div className={`sm:px-1 dark:border-dark-2 border-2 rounded-lg flex flex-col justify-start items-start overflow-hidden max-w-[8rem] xxs:max-w-[10rem]`}>
      <p className="px-0.5 xs:px-1 text-xs text-darken font-light">Sort By</p>
      <select
          className={`xs:px-1 bg-transparent max-xs:text-xs`}

                onChange={(e) => {
                  const sortBy = e.target.value;
                  switch (sortBy) {
                    case "team":
                      setSorts(sortBy, "asc");
                      break;
                    case "surname":
                      setSorts("player.surname", "asc");
                      break;
                    case "forename":
                      setSorts("player.forename", "asc");
                      break;
                    case "institution":
                      setSorts(sortBy, "asc");
                      break;
                    case "classification":
                      setSorts(sortBy, "asc");
                      break;
                    case "reg_date":
                      setSorts("reg_date", "asc");
                      break;
                    case "gp":
                      setSorts(sortBy, "desc", false);
                      break;
                    case "points":
                      setSorts(sortBy, "desc", false);
                      break;
                    case "g":
                      setSorts(sortBy, "desc", false);
                      break;
                    case "a":
                      setSorts(sortBy, "desc", false);
                      break;
                    case "pims":
                      setSorts(sortBy, "desc", false);
                      break;
                    default:
                      // Handle default case if needed
                      break;
                  }
                }}
              >
                <option value="surname">Surname</option>
                <option value="forename">Forename</option>
                <option value="team">Team</option>
                <option value="institution">Institution</option>
                <option value="classification">Status</option>
                <option value="reg_date">First Reg</option>
                <option value="gp">Games</option>
                <option value="points">Points</option>
                <option value="g">Goals</option>
                <option value="a">Assists</option>
                <option value="pims">PIMs</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table of players */}
        <div className="flex flex-wrap justify-center gap-x-2 xs:gap-x-3 gap-y-2 max-xs:px-1 mb-10">
          {/* FOR EACH PLAYER */}
          {visiblePlayers}
        </div>
      </>
    )
  );
};
export default ClubRoster;
